/**
 * The two halves of the Add/Edit Transaction sheet's date handling, kept together and kept pure.
 *
 * The sheet is a GHANA WALL-CLOCK editor: the date and time an operator sees and types are the
 * Accra calendar day and clock, exactly as the bank wrote them, and `Africa/Accra` is UTC+0 all year
 * round with no DST — so those parts ARE the UTC parts (the same reasoning, and the same `Date.UTC`
 * mechanic, as `buildTimestamp` in `@/lib/gt-repayments`).
 *
 * READ and WRITE are one decision, never two. Before this pair, both sides went through the
 * browser's zone — `format(new Date(transaction_at), 'yyyy-MM-dd')` on the way in and
 * `new Date(\`${date}T${time}:00\`)` on the way out — which cancelled out into a self-consistent
 * round trip while showing a US operator the wrong day. Converting only the prefill would have
 * broken that cancellation and moved every edited transaction by the viewer's offset, so the two
 * functions live in one module with one shared invariant, asserted in the sibling test:
 *
 *     fieldsToTransactionAt(transactionAtToFields(iso)) === iso    (in ANY process timezone)
 *
 * for any instant on a whole minute — which is the only precision the form's `<input type="time">`
 * can carry. Sub-minute precision is truncated to :00, exactly as the previous implementation did;
 * the sheet shows minutes, so it saves minutes.
 */

import { BUSINESS_TZ } from '@/lib/format';

/**
 * `hourCycle: 'h23'` (not `hour12: false`, which yields "24" for midnight in some ICU builds) so the
 * hour field is always the 00–23 shape an `<input type="time">` expects.
 */
const BUSINESS_FIELD_PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

/** The form's two date fields: an empty pair means "no date on this transaction". */
export interface TransactionAtFields {
    /** `yyyy-MM-dd` — the Accra calendar day. */
    date: string;
    /** `HH:mm` — the Accra wall-clock time, 24-hour. */
    time: string;
}

const EMPTY_FIELDS: TransactionAtFields = { date: '', time: '' };

/**
 * A stored `transaction_at` instant → the Accra day and clock the form edits.
 *
 * Empty fields for a missing or unparseable value, which the sheet already treats as "no date"
 * (`canSubmit` requires both).
 */
export function transactionAtToFields(value: string | Date | null | undefined): TransactionAtFields {
    if (value === null || value === undefined) return EMPTY_FIELDS;
    const instant = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(instant.getTime())) return EMPTY_FIELDS;

    const parts = BUSINESS_FIELD_PARTS_FORMATTER.formatToParts(instant);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? '';

    return {
        date: `${part('year')}-${part('month')}-${part('day')}`,
        time: `${part('hour')}:${part('minute')}`,
    };
}

/**
 * The form's Accra day and clock → the instant to store, as an ISO string.
 *
 * `Date.UTC` on the parts, never `new Date('2026-08-10T00:30:00')` — the engine resolves that
 * against the BROWSER's zone, so a 00:30 Accra payment typed in New York would be stored as
 * 05:30Z, i.e. the same wall time on a different instant, and read back as a different day.
 *
 * Null when either field is missing or misshapen, so a malformed pair can never be silently
 * coerced into "now"; the caller keeps its own guard (`canSubmit`) as the user-visible one.
 */
export function fieldsToTransactionAt(date: string, time: string): string | null {
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    const clock = /^(\d{2}):(\d{2})$/.exec(time);
    if (!day || !clock) return null;

    const year = Number(day[1]);
    const monthIndex = Number(day[2]) - 1;
    const dayOfMonth = Number(day[3]);
    const hours = Number(clock[1]);
    const minutes = Number(clock[2]);
    if (monthIndex > 11 || dayOfMonth > 31 || hours > 23 || minutes > 59) return null;

    const instant = new Date(Date.UTC(year, monthIndex, dayOfMonth, hours, minutes, 0, 0));
    return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}
